import { TaskStatus, TaskTemplate } from "@hg/recall-frame"
import cls from "classnames"
import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"

import lotteryStyles from "@/components/Lottery/UI/index.module.scss"
import { Modal } from "@/components/Modal"
import StrokeText from "@/components/StrokeText"
import { useCheckLogin } from "@/hooks"
import { i18n } from "@/i18n"
import { completeTask, getTaskStatus } from "@/services/core"
import { useRoleInfo } from "@/user/session"
import { collectRecallShare } from "@/utils/event"

import styles from "./index.module.scss"

const CID_TO_TEMPLATE: Record<string, TaskTemplate> = {
    T_FpRecallShare_20261008: TaskTemplate.SHARE,
    T_FpInvite_20261008: TaskTemplate.INVITE,
    T_FpMemberLogin5_20261008: TaskTemplate.MEMBER_LOGIN_TIMES,
}

const TEMPLATE_TO_CID: Record<TaskTemplate, string> = {
    [TaskTemplate.SHARE]: "T_FpRecallShare_20261008",
    [TaskTemplate.INVITE]: "T_FpInvite_20261008",
    [TaskTemplate.MEMBER_LOGIN_TIMES]: "T_FpMemberLogin5_20261008",
    [TaskTemplate.CASUAL]: "",
    [TaskTemplate.LOGIN_TIMES]: "",
    [TaskTemplate.AP_COST]: "",
    [TaskTemplate.MEMBER_AP_COST]: "",
}

export const TASK_TEMPLATE_MAP: Record<
    string,
    {
        desc: string
        rewards: {
            key: string
            amount: number
        }[]
    }
> = {
    [TaskTemplate.SHARE]: {
        desc: i18n.getRecall("task.share"),
        rewards: [
            {
                key: "4001",
                amount: 5000,
            },
        ],
    },
    [TaskTemplate.INVITE]: {
        desc: i18n.getRecall("task.invite"),
        rewards: [
            {
                key: "4003",
                amount: 300,
            },
            {
                key: "chance",
                amount: 1,
            },
        ],
    },
    [TaskTemplate.MEMBER_LOGIN_TIMES]: {
        desc: i18n.getRecall("task.login"),
        rewards: [
            {
                key: "4003",
                amount: 300,
            },
            {
                key: "chance",
                amount: 2,
            },
        ],
    },
}

// ─── 领取成功弹窗 ─────────────────────────────────────────────

interface ClaimRewardModalProps {
    template: TaskTemplate | null
    onClose: () => void
}

const ClaimRewardModal: React.FC<ClaimRewardModalProps> = ({ template, onClose }) => {
    const task = template ? TASK_TEMPLATE_MAP[template] : null

    return (
        <Modal
            open={!!template}
            onCancel={onClose}
            onOk={onClose}
            showCancelBtn={false}
            size="medium"
        >
            {task && (
                <>
                    <div className={cls(lotteryStyles.congratulations, lotteryStyles.medium)} />
                    <div className={styles.claimRewards} data-count={task.rewards.length}>
                        <div className={styles.bg} />
                        {task.rewards.map((reward) => (
                            <div key={reward.key} className={styles.reward}>
                                <div className={styles.icon} data-key={reward.key} />
                                <StrokeText
                                    text={`${reward.key === "chance" ? i18n.getRecall("task.chance") : ""}x${reward.amount}`}
                                    fontFamily="source-han-sans-japanese"
                                    fontWeight={700}
                                    fontSize={30}
                                    fillColor="#fffeee"
                                    borderWidth={3}
                                    borderColor="#8f7254"
                                    offsetY={0}
                                    padding={3}
                                    baselineAligned
                                    className={styles.text}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Modal>
    )
}

// ─── 单个任务 ─────────────────────────────────────────────────

interface TaskProps {
    template: TaskTemplate
    taskCid: string
    status: TaskStatus
    onClick: () => void
    onClaim: (template: TaskTemplate) => void
    onClaimComplete: () => void
}

const Task: React.FC<TaskProps> = (props) => {
    const { template, taskCid, status, onClick, onClaim, onClaimComplete } = props

    const user = useRoleInfo()
    const checkLogin = useCheckLogin()

    const task = TASK_TEMPLATE_MAP[template]
    const isDone = status === TaskStatus.COMPLETED
    const isFinished = status === TaskStatus.ACCOMPLISHED || isDone

    const handleClick = useCallback(() => {
        // 已领取：不响应
        if (isDone) return

        // 未登录：弹登录框
        if (!user) {
            checkLogin()
            return
        }

        // 待领取（10）：调领取接口
        if (status === TaskStatus.ACCOMPLISHED) {
            completeTask(taskCid).then((res) => {
                if (res.code === 0) {
                    onClaim(template)
                    onClaimComplete()
                }
            })
            return
        }

        // 未完成（0）：执行前往动作
        if (template === TaskTemplate.SHARE) {
            collectRecallShare(`mission_${template}`)
        }
        onClick()
    }, [template, taskCid, status, isDone, user, checkLogin, onClick, onClaim, onClaimComplete])

    const text = useMemo(() => {
        if (status === TaskStatus.COMPLETED) return "task.finished"
        if (status === TaskStatus.ACCOMPLISHED) return "task.claim"
        return "task.unfinished"
    }, [status])

    return (
        <div className={styles.task}>
            <div
                className={styles.desc}
                dangerouslySetInnerHTML={{ __html: task.desc }}
            />
            <div className={styles.rewards} data-count={task.rewards.length}>
                <div className={styles.bg} />
                {task.rewards.map((reward) => (
                    <div key={reward.key} className={styles.reward}>
                        <div className={styles.icon} data-key={reward.key} />
                        <StrokeText
                            text={`${reward.key === "chance" ? i18n.getRecall("task.chance") : ""}x${reward.amount}`}
                            fontFamily="source-han-sans-japanese"
                            fontWeight={700}
                            fontSize={25}
                            fillColor="#fffeee"
                            borderWidth={3}
                            borderColor="#8f7254"
                            offsetY={0}
                            padding={3}
                            baselineAligned
                            className={styles.text}
                        />
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={handleClick}
                disabled={isDone}
                className={cls(isFinished && styles.finished)}
            >
                {i18n
                    .getRecall(text)
                    .split("")
                    .map((char, index) => (
                        <StrokeText
                            key={index}
                            text={char}
                            fontFamily="source-han-sans-japanese"
                            fontWeight={700}
                            fontSize={isDone ? 24 : 37}
                            fillColor="#ffffff"
                            borderWidth={3}
                            borderColor="#564426"
                            offsetY={0}
                            padding={3}
                            baselineAligned
                            className={styles.text}
                        />
                    ))}
            </button>
        </div>
    )
}

// ─── 任务列表 ─────────────────────────────────────────────────

const TASK_TEMPLATES = [
    TaskTemplate.SHARE,
    TaskTemplate.INVITE,
    TaskTemplate.MEMBER_LOGIN_TIMES,
]

interface TaskListProps {
    onOpenShare: () => void
    onOpenProgress: () => void
    refreshKey?: number
    onClaimComplete: () => void
}

export const TaskList: React.FC<TaskListProps> = ({ onOpenShare, onOpenProgress, refreshKey, onClaimComplete }) => {
    const user = useRoleInfo()
    const [statusMap, setStatusMap] = useState<Partial<Record<TaskTemplate, TaskStatus>>>({})
    const [claimedTemplate, setClaimedTemplate] = useState<TaskTemplate | null>(null)

    useEffect(() => {
        if (!user) return
        getTaskStatus().then((res) => {
            if (res.code === 0) {
                const map: Partial<Record<TaskTemplate, TaskStatus>> = {}
                for (const item of res.data) {
                    const template = CID_TO_TEMPLATE[item.cid]
                    if (template) {
                        map[template] = item.status as TaskStatus
                    }
                }
                setStatusMap(map)
            }
        })
    }, [user, refreshKey])

    const getOnClick = (template: TaskTemplate) => {
        if (template === TaskTemplate.SHARE) return onOpenShare
        if (template === TaskTemplate.INVITE || template === TaskTemplate.MEMBER_LOGIN_TIMES) return onOpenProgress
        return () => {}
    }

    return (
        <div className={styles.taskList}>
            <div className={styles.scroll}>
                {TASK_TEMPLATES.map((template) => (
                    <Task
                        key={template}
                        template={template}
                        taskCid={TEMPLATE_TO_CID[template]}
                        status={statusMap[template] ?? TaskStatus.INITIAL}
                        onClick={getOnClick(template)}
                        onClaim={setClaimedTemplate}
                        onClaimComplete={onClaimComplete}
                    />
                ))}
            </div>
            <ClaimRewardModal
                template={claimedTemplate}
                onClose={() => setClaimedTemplate(null)}
            />
        </div>
    )
}

export default TaskList
